import { Group, rem, Stack, Text } from '@mantine/core'
import { forwardRef, memo, useLayoutEffect, useMemo, useRef, type ReactNode } from 'react'

import { RampingNumberText } from '~/boggle/ui/atoms/RampingNumberText'
import {
  ComboFeedbackLayer,
  ScoreFeedbackLayer,
  getBoggleScoreHudValueClassName,
} from '~/boggle/ui/run/ScoreFeedback'
import type {
  useBoggleComboFeedback,
  useBoggleScoreFeedback,
} from '~/boggle/ui/run/ScoreFeedback'
import {
  RUN_RESULTS_SCORE_RAMP_BASE_MS,
  RUN_RESULTS_SCORE_RAMP_MIN_MS,
  RUN_RESULTS_SCORE_RAMP_REFERENCE_DELTA,
} from '~/boggle/ui/run/runResultsScoreRamp'
import classes from './RunHud.module.css'

const TILES_CLEARED_RAMP_MS = 150

function mergeClassNames(...classNames: Array<false | null | string | undefined>) {
  return classNames.filter(Boolean).join(' ')
}

function HudValueWithPulseRestart({
  pulseClassName,
  pulseToken,
  children,
}: {
  pulseClassName?: string
  pulseToken: number
  children: ReactNode
}) {
  const wrapRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    if (!pulseClassName) {
      return
    }
    const el = wrapRef.current
    if (!el) {
      return
    }
    el.style.animation = 'none'
    void el.offsetWidth
    el.style.animation = ''
  }, [pulseClassName, pulseToken])

  return (
    <div ref={wrapRef} className={mergeClassNames(classes.pulseWrap, pulseClassName)}>
      {children}
    </div>
  )
}

export function getBoggleIntervalHudStatus({
  intervalCount,
  intervalsRemaining,
  isInOvertime,
}: {
  intervalCount: null | number
  intervalsRemaining: null | number
  isInOvertime: boolean
}) {
  if (intervalCount === null || intervalCount <= 1 || intervalsRemaining === null) {
    return null
  }

  const intervalIndex = intervalCount - intervalsRemaining + 1

  return isInOvertime
    ? `Interval ${intervalIndex} overtime`
    : `Interval ${intervalIndex} of ${intervalCount}`
}

const HeaderCounter = memo(
  forwardRef<
    HTMLDivElement,
    {
      label: string
      labelColor?: string
      primary?: boolean
      pulseKey?: string
      value: ReactNode
      valueClassName?: string
      valueColor?: string
    }
  >(function HeaderCounter(
    { label, labelColor = '#caa46a', primary = false, pulseKey, value, valueClassName, valueColor },
    ref,
  ) {
    return (
      <Stack align="center" gap={6}>
        <Text c={labelColor} fz={14} lts="0.12em" tt="uppercase" ta="center">
          {label}
        </Text>
        <Text
          c={valueColor}
          className={valueClassName}
          component="div"
          fz={primary ? 45 : 32}
          fw={700}
          key={pulseKey}
          ref={ref}
        >
          {value}
        </Text>
      </Stack>
    )
  }),
)

const IntervalCounterValue = memo(function IntervalCounterValue({
  intervalCount,
  intervalIndex,
}: {
  intervalCount: number
  intervalIndex: number
}) {
  return (
    <Group gap={4} align="center">
      <Text span fw={400} fz={24}>
        {intervalIndex}
      </Text>
      <Text span c="rgba(242, 232, 217, 0.5)" fw={400} fz={18}>
        {' / '}
      </Text>
      <Text span fw={700} fz={28}>
        {intervalCount}
      </Text>
    </Group>
  )
})

export function RunHud({
  combo,
  comboFeedbackEvents,
  intervalCounterIndex,
  isLastInterval,
  meterSlot,
  reserveRunLayout,
  resultsInstantScoreRamp = false,
  resultsScore = 0,
  resultsScoreMaxValueVisual = 100,
  scoreFeedback,
  showComboCounter,
  showEndedSummary,
  showIntervalCounter,
  showResultsPhase = false,
  timerIntervalCount,
  timerSlot,
  tilesCleared,
}: {
  combo: number
  comboFeedbackEvents: ReturnType<typeof useBoggleComboFeedback>
  intervalCounterIndex: null | number
  isLastInterval: boolean
  /** Meter gauge / placeholder — owned by `RunMeterIsland`. */
  meterSlot: ReactNode | null
  reserveRunLayout: boolean
  /** When true during results phase, snap the Score counter instantly (skipped choreography). */
  resultsInstantScoreRamp?: boolean
  /** Running score total during results (drives the same counter that showed Tiles Cleared during play). */
  resultsScore?: number
  /** `maxValueVisual` for the score ramp in results phase (typically the final summary total). */
  resultsScoreMaxValueVisual?: number
  scoreFeedback: ReturnType<typeof useBoggleScoreFeedback>
  showComboCounter: boolean
  showEndedSummary: boolean
  showIntervalCounter: boolean
  /** Cross-fade play HUD elements out + relabel the Tiles Cleared counter to Score. */
  showResultsPhase?: boolean
  timerIntervalCount: null | number
  /** Timer ring / gold-rush label / placeholder — owned by `RunTimerIsland`. */
  timerSlot: ReactNode | null
  tilesCleared: number
}) {
  const playFadeClassName = showResultsPhase
    ? classes.playElementsHidden
    : classes.playElementsVisible

  const scoreHudTilesPulseClassName = getBoggleScoreHudValueClassName(
    scoreFeedback.scorePulseToken,
  )

  const counterValue = useMemo(
    () => (
      <HudValueWithPulseRestart
        pulseClassName={showResultsPhase ? undefined : scoreHudTilesPulseClassName}
        pulseToken={scoreFeedback.scorePulseToken}
      >
        <RampingNumberText
          behavior="standard"
          duration={
            showResultsPhase
              ? resultsInstantScoreRamp
                ? 0
                : RUN_RESULTS_SCORE_RAMP_BASE_MS
              : TILES_CLEARED_RAMP_MS
          }
          durationScaleReferenceValue={
            showResultsPhase && !resultsInstantScoreRamp
              ? RUN_RESULTS_SCORE_RAMP_REFERENCE_DELTA
              : undefined
          }
          minScaledDuration={
            showResultsPhase && !resultsInstantScoreRamp
              ? RUN_RESULTS_SCORE_RAMP_MIN_MS
              : showResultsPhase
                ? 0
                : undefined
          }
          maxValueVisual={showResultsPhase ? Math.max(1, resultsScoreMaxValueVisual) : 100}
          value={showResultsPhase ? resultsScore : tilesCleared}
        />
      </HudValueWithPulseRestart>
    ),
    [
      resultsInstantScoreRamp,
      resultsScore,
      resultsScoreMaxValueVisual,
      scoreFeedback.scorePulseToken,
      scoreHudTilesPulseClassName,
      showResultsPhase,
      tilesCleared,
    ],
  )

  const wordScoreFeedbackEvents = useMemo(
    () => scoreFeedback.events.filter(event => event.variant === 'tiles'),
    [scoreFeedback.events],
  )

  const intervalCounterNode = useMemo(() => {
    if (
      !showIntervalCounter ||
      timerIntervalCount === null ||
      intervalCounterIndex === null
    ) {
      return null
    }

    return (
      <HeaderCounter
        label="Interval"
        value={
          <IntervalCounterValue
            intervalCount={timerIntervalCount}
            intervalIndex={intervalCounterIndex}
          />
        }
        valueColor={isLastInterval ? 'var(--mantine-color-red-4)' : '#f0c85a'}
      />
    )
  }, [
    intervalCounterIndex,
    isLastInterval,
    showIntervalCounter,
    timerIntervalCount,
  ])

  const comboCounterNode = useMemo(() => {
    if (!showComboCounter) {
      return null
    }

    return (
      <div className={classes.comboCounterAnchor}>
        <HeaderCounter
          label="Bonus"
          pulseKey={`combo-${combo}`}
          value={combo === 0 ? '0' : `+${combo}`}
          valueColor={combo === 0 ? '#ffffff' : '#e38c5c'}
        />
        <ComboFeedbackLayer events={comboFeedbackEvents} />
      </div>
    )
  }, [combo, comboFeedbackEvents, showComboCounter])

  const timerSlotNode = useMemo(() => {
    if (timerSlot === null) {
      return null
    }

    return (
      <div
        className={mergeClassNames(
          classes.gaugeSlot,
          classes.gaugeSlotEnd,
          playFadeClassName,
        )}
      >
        {timerSlot}
      </div>
    )
  }, [playFadeClassName, timerSlot])

  const meterSlotNode = useMemo(() => {
    if (meterSlot === null) {
      return null
    }

    return (
      <div
        className={mergeClassNames(
          classes.gaugeSlot,
          classes.gaugeSlotStart,
          playFadeClassName,
        )}
      >
        {meterSlot}
      </div>
    )
  }, [meterSlot, playFadeClassName])

  return (
    <Group
      align="flex-start"
      justify="center"
      wrap="nowrap"
      className={mergeClassNames(classes.root, showEndedSummary && classes.rootEndedSummary)}
    >
      <Group
        align="flex-start"
        wrap="nowrap"
        className={mergeClassNames(classes.topCounters, playFadeClassName)}
      >
        {intervalCounterNode}
        {comboCounterNode}
      </Group>
      <Group
        align="center"
        justify="center"
        wrap="nowrap"
        className={classes.mainRow}
      >
        {timerSlotNode}
        {!showEndedSummary ? (
          <Group align="center" justify="center" className={classes.centerCounter}>
            <HeaderCounter
              label={showResultsPhase ? 'Score' : 'Tiles Cleared'}
              primary
              value={counterValue}
            />
            <ScoreFeedbackLayer
              events={wordScoreFeedbackEvents}
              placement="hudTiles"
            />
          </Group>
        ) : null}
        {meterSlotNode}
      </Group>
    </Group>
  )
}
